import React from 'react';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { ModalBody, ModalHeader, Modalv2 } from '@itandi/itandi-bb-ui';
import { ConstructionForm } from '~/components/common/constructions/ConstructionForm';
import { isSuccess } from '~/utils/api/api_handler';
import type { NskResponse } from '~/models/Api';
import type { ConstructionItem } from '~/models/ConstructionItem';
import type {
  DealConstruction,
  DealConstructionEdit
} from '~/models/DealConstruction';
import { validationSchemaConstruction } from '~/models/DealConstruction';
import { ConstructionFormFooter } from '../ConstructionFormFooter';

type Props = {
  dealConstruction: DealConstruction;
  constructionItems: ConstructionItem[];
  handleMutateConstructions: (() => Promise<void>) | null;
  handleClose: () => void;
  handleUpdate: (
    submitValue: DealConstructionEdit,
    estimationId: number | null,
    historyId: number | null
  ) => Promise<NskResponse<void>>;
  disabled: boolean;
};

export const ConstructionEditModal: React.FC<Props> = ({
  dealConstruction,
  handleMutateConstructions,
  constructionItems,
  handleClose,
  handleUpdate,
  disabled
}: Props) => {
  const methods = useForm<DealConstructionEdit>({
    defaultValues: dealConstruction,
    mode: 'onBlur',
    resolver: yupResolver(validationSchemaConstruction)
  });
  const executeUpdateDealConstruction = React.useCallback((): Promise<
    NskResponse<void>
  > => {
    // 初回の見積りの場合は、dealEstimationを参照する
    if (dealConstruction.type === 'DealEstimation') {
      methods.setValue('operationType', 'edit');
      return handleUpdate(methods.getValues(), dealConstruction.id, null);
    }
    // operationTypeがaddの場合は、必ずDealEstimationHistoryが存在している
    if (dealConstruction.type === 'DealEstimationHistory') {
      const { operationType, dealEstimation } = dealConstruction;
      if (operationType === 'add') {
        return handleUpdate(methods.getValues(), null, dealConstruction.id);
      }
      if (dealEstimation !== null) {
        methods.setValue('operationType', 'edit');
        return handleUpdate(methods.getValues(), dealEstimation.id, null);
      }
    }
    return handleUpdate(methods.getValues(), dealConstruction.id, null);
  }, [dealConstruction, handleUpdate, methods]);
  const onClickSubmit = React.useCallback(() => {
    methods.handleSubmit(async () => {
      const response = await executeUpdateDealConstruction();
      if (isSuccess(response)) {
        await handleMutateConstructions?.();
        methods.reset();
        handleClose();
        toast.success('見積りを更新しました');
        return;
      }
      toast.error('エラーが発生しました');
      handleClose();
      methods.reset();
    })();
  }, [
    executeUpdateDealConstruction,
    handleClose,
    handleMutateConstructions,
    methods
  ]);

  return (
    <Modalv2 isOpen onClose={handleClose}>
      <ModalHeader title="見積りの修正" />
      <ModalBody>
        <FormProvider {...methods}>
          <ConstructionForm
            constructionItems={constructionItems}
            disabled={disabled}
          />
          <ConstructionFormFooter
            disabled={disabled || methods.formState.isSubmitting}
            onClickSubmit={onClickSubmit}
            type="edit"
          />
        </FormProvider>
      </ModalBody>
    </Modalv2>
  );
};
